<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import { useFunnelManager } from 'dashboard/composables/kanban/useFunnelManager';
import { CONVERSATION_PRIORITY } from 'shared/constants/messages';
import ConversationFunnelMappingsAPI from 'dashboard/api/conversationFunnelMappings';
import KanbanAPI from 'dashboard/api/kanban';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import LabelBox from 'dashboard/routes/dashboard/conversation/labels/LabelBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TerminalStageCloseModal from '../TerminalStageCloseModal.vue';
import { terminalStageKind } from 'dashboard/helper/funnelStages';
import ConversationAppointments from 'dashboard/components/widgets/conversation/appointments/ConversationAppointments.vue';

// Painel de "Ações da conversa" da tela do negócio. Ligado direto à conversa
// (props), sem depender do getSelectedChat estar pronto — evita o crash de
// `meta.assignee` na aba padrão. Usa os MESMOS store actions do inbox.
const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const { agentsList } = useAgentsList();
const currentUser = useMapGetter('getCurrentUser');
const teams = useMapGetter('teams/getTeams');

// tradução com fallback PT fixo (sobrevive a chunk de locale velho em cache).
const tp = (key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};

const conversationId = computed(() => props.conversation?.id);

// --- Funil e etapa (movido do antigo "Funis Múltiplos") ---
const { funnels, loadFunnels } = useFunnelManager();
const mappings = ref([]);
const showAddFunnel = ref(false);
const addFunnelId = ref(null);
const addStage = ref(null);
const savingFunnel = ref(false);

const loadMappings = async () => {
  try {
    const { data } = await ConversationFunnelMappingsAPI.getMappings(
      conversationId.value
    );
    mappings.value = Array.isArray(data) ? data : data?.mappings || [];
  } catch {
    mappings.value = [];
  }
};

const stagesForFunnel = funnelId => {
  const funnel = (funnels.value || []).find(f => f.id === funnelId);
  if (!funnel?.stages) return [];
  return Object.entries(funnel.stages)
    .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
    .map(([key, stage]) => ({ key, label: stage.label || key }));
};

const availableFunnels = computed(() => {
  const mapped = mappings.value.map(m => m.funnel_id);
  return (funnels.value || []).filter(f => f.active && !mapped.includes(f.id));
});

const addFunnelStages = computed(() =>
  addFunnelId.value ? stagesForFunnel(Number(addFunnelId.value)) : []
);

// `source` evita eco: quem originou a mudança já atualizou o próprio estado.
const notifyFunnelChange = () => {
  window.dispatchEvent(
    new CustomEvent('conversation-funnel-updated', {
      detail: { source: 'panel' },
    })
  );
};

const onFunnelUpdated = event => {
  if (event.detail?.source !== 'panel') loadMappings();
};

// Etapa terminal (Ganho/Perdido) abre o fechamento em vez de só mover.
const pendingClose = ref(null);
const onDealClosed = async () => {
  pendingClose.value = null;
  await loadMappings();
  notifyFunnelChange();
};

const moveStage = async (mapping, stageName, selectEl) => {
  if (!stageName || stageName === mapping.stage_name) return;
  const funnel = (funnels.value || []).find(f => f.id === mapping.funnel_id);
  const kind = terminalStageKind(funnel, stageName);
  if (kind && (mapping.deal_status || 'open') !== kind) {
    pendingClose.value = { mapping, kind };
    if (selectEl) selectEl.value = mapping.stage_name;
    return;
  }
  try {
    await ConversationFunnelMappingsAPI.moveToStage(
      conversationId.value,
      mapping.id,
      stageName
    );
    const stage = stagesForFunnel(mapping.funnel_id).find(
      s => s.key === stageName
    );
    mapping.stage_name = stageName;
    mapping.stage_label = stage?.label;
    useAlert(tp('MULTIPLE_FUNNELS.MESSAGES.STAGE_UPDATED', 'Etapa atualizada'));
    notifyFunnelChange();
  } catch (error) {
    if (error?.response?.data?.code === 'close_required') {
      pendingClose.value = { mapping, kind: error.response.data.kind };
      if (selectEl) selectEl.value = mapping.stage_name;
      return;
    }
    if (selectEl) selectEl.value = mapping.stage_name;
    useAlert(tp('MULTIPLE_FUNNELS.MESSAGES.ERROR', 'Erro ao atualizar'));
  }
};

const confirmAddFunnel = async () => {
  if (!addFunnelId.value || !addStage.value) return;
  savingFunnel.value = true;
  try {
    const { data } = await ConversationFunnelMappingsAPI.addToFunnel(
      conversationId.value,
      Number(addFunnelId.value),
      addStage.value
    );
    if (data.mapping) mappings.value.push(data.mapping);
    showAddFunnel.value = false;
    addFunnelId.value = null;
    addStage.value = null;
    useAlert(tp('MULTIPLE_FUNNELS.MESSAGES.ADDED', 'Adicionado ao funil'));
    notifyFunnelChange();
  } catch {
    useAlert(tp('MULTIPLE_FUNNELS.MESSAGES.ERROR', 'Erro ao adicionar'));
  } finally {
    savingFunnel.value = false;
  }
};

const removeFromFunnel = async mapping => {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      tp('MULTIPLE_FUNNELS.REMOVE.CONFIRM', 'Remover deste funil?')
    )
  )
    return;
  try {
    await ConversationFunnelMappingsAPI.removeFromFunnel(
      conversationId.value,
      mapping.id
    );
    mappings.value = mappings.value.filter(m => m.id !== mapping.id);
    useAlert(tp('MULTIPLE_FUNNELS.MESSAGES.REMOVED', 'Removido do funil'));
    notifyFunnelChange();
  } catch {
    useAlert(tp('MULTIPLE_FUNNELS.MESSAGES.ERROR', 'Erro ao remover'));
  }
};

onMounted(() => {
  loadMappings();
  loadFunnels();
  window.addEventListener('conversation-funnel-updated', onFunnelUpdated);
});

onUnmounted(() => {
  window.removeEventListener('conversation-funnel-updated', onFunnelUpdated);
});

// --- Próxima ação (data + tipo + nota), guardada no metadata do negócio ---
const primaryMapping = computed(() => mappings.value[0] || null);
const nextAction = computed(
  () => primaryMapping.value?.metadata?.next_action || null
);

const naTypes = computed(() => [
  { id: 'call', label: tp('DEAL_RECORD.NEXT_ACTION.TYPES.CALL', 'Ligar') },
  {
    id: 'message',
    label: tp('DEAL_RECORD.NEXT_ACTION.TYPES.MESSAGE', 'Mandar mensagem'),
  },
  {
    id: 'meeting',
    label: tp('DEAL_RECORD.NEXT_ACTION.TYPES.MEETING', 'Reunião'),
  },
  { id: 'other', label: tp('DEAL_RECORD.NEXT_ACTION.TYPES.OTHER', 'Outro') },
]);

const editingNextAction = ref(false);
const savingNextAction = ref(false);
const naForm = ref({ due_on: '', type: 'call', note: '' });

// Status da data: vencida / hoje / futura (para colorir o card).
const naStatus = computed(() => {
  const na = nextAction.value;
  if (!na || na.done || !na.due_on) return null;
  const due = new Date(`${na.due_on}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (Number.isNaN(due.getTime())) return null;
  if (due < today) return 'overdue';
  if (due.getTime() === today.getTime()) return 'today';
  return 'upcoming';
});

const naTypeLabel = type =>
  naTypes.value.find(t2 => t2.id === type)?.label || type;

const formatNaDate = value => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const openNextActionEdit = () => {
  const na = nextAction.value;
  naForm.value = na
    ? { due_on: na.due_on || '', type: na.type || 'call', note: na.note || '' }
    : { due_on: '', type: 'call', note: '' };
  editingNextAction.value = true;
};

const persistNextAction = async payload => {
  const mapping = primaryMapping.value;
  if (!mapping) return;
  await KanbanAPI.updateDealMetadata(mapping.funnel_id, conversationId.value, {
    next_action: payload,
  });
  mapping.metadata = { ...(mapping.metadata || {}) };
  if (payload === null) delete mapping.metadata.next_action;
  else mapping.metadata.next_action = { ...payload };
};

const saveNextAction = async () => {
  if (!primaryMapping.value || !naForm.value.due_on) return;
  savingNextAction.value = true;
  try {
    await persistNextAction({ ...naForm.value, done: false });
    editingNextAction.value = false;
    useAlert(tp('DEAL_RECORD.NEXT_ACTION.SAVED', 'Próxima ação definida'));
  } catch {
    useAlert(tp('MULTIPLE_FUNNELS.MESSAGES.ERROR', 'Erro ao salvar'));
  } finally {
    savingNextAction.value = false;
  }
};

const markNextActionDone = async () => {
  try {
    await persistNextAction({ ...nextAction.value, done: true });
    useAlert(tp('DEAL_RECORD.NEXT_ACTION.DONE', 'Ação concluída'));
  } catch {
    useAlert(tp('MULTIPLE_FUNNELS.MESSAGES.ERROR', 'Erro'));
  }
};

const clearNextAction = async () => {
  try {
    await persistNextAction(null);
    editingNextAction.value = false;
  } catch {
    useAlert(tp('MULTIPLE_FUNNELS.MESSAGES.ERROR', 'Erro'));
  }
};
const assignedAgent = computed(
  () => props.conversation?.meta?.assignee ?? null
);
const assignedTeam = computed(() => props.conversation?.meta?.team ?? null);

const teamsList = computed(() =>
  assignedTeam.value
    ? [{ id: 0, name: t('TEAMS_SETTINGS.LIST.NONE') }, ...(teams.value || [])]
    : teams.value || []
);

const priorityOptions = computed(() => [
  {
    id: null,
    name: t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
    thumbnail: '/assets/images/dashboard/priority/none.svg',
  },
  {
    id: CONVERSATION_PRIORITY.URGENT,
    name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
    thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.URGENT}.svg`,
  },
  {
    id: CONVERSATION_PRIORITY.HIGH,
    name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
    thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.HIGH}.svg`,
  },
  {
    id: CONVERSATION_PRIORITY.MEDIUM,
    name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
    thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.MEDIUM}.svg`,
  },
  {
    id: CONVERSATION_PRIORITY.LOW,
    name: t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
    thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.LOW}.svg`,
  },
]);

const assignedPriority = computed(
  () =>
    priorityOptions.value.find(
      o => o.id === (props.conversation?.priority ?? null)
    ) || priorityOptions.value[0]
);

const showSelfAssign = computed(
  () => !assignedAgent.value || assignedAgent.value.id !== currentUser.value?.id
);

const onAssignAgent = agent => {
  const isSame = assignedAgent.value && assignedAgent.value.id === agent.id;
  const chosen = isSame ? null : agent;
  store.dispatch('setCurrentChatAssignee', chosen);
  store
    .dispatch('assignAgent', {
      conversationId: conversationId.value,
      agentId: chosen ? chosen.id : 0,
    })
    .then(() => useAlert(t('CONVERSATION.CHANGE_AGENT')));
};

const onSelfAssign = () => {
  const u = currentUser.value;
  if (!u) return;
  onAssignAgent({ ...u, thumbnail: u.avatar_url });
};

const onAssignTeam = team => {
  const isSame = assignedTeam.value && assignedTeam.value.id === team.id;
  const chosen = isSame ? null : team;
  store.dispatch('setCurrentChatTeam', {
    team: chosen,
    conversationId: conversationId.value,
  });
  store
    .dispatch('assignTeam', {
      conversationId: conversationId.value,
      teamId: chosen ? chosen.id : 0,
    })
    .then(() => useAlert(t('CONVERSATION.CHANGE_TEAM')));
};

const onAssignPriority = item => {
  const isSame =
    assignedPriority.value && assignedPriority.value.id === item.id;
  const priority = isSame ? null : (item?.id ?? null);
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId: conversationId.value,
  });
  store
    .dispatch('assignPriority', {
      conversationId: conversationId.value,
      priority,
    })
    .then(() =>
      useAlert(
        t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
          priority: item?.name,
          conversationId: conversationId.value,
        })
      )
    );
};
</script>

<template>
  <div
    class="flex flex-col border divide-y rounded-xl border-n-weak divide-n-weak bg-n-solid-1 multiselect-wrap--small"
  >
    <!-- Próxima ação (destaque no topo) -->
    <div v-if="primaryMapping" class="p-3">
      <span
        class="flex items-center gap-1.5 mb-2 text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
      >
        <span class="i-lucide-alarm-clock size-3.5" />
        {{ tp('DEAL_RECORD.NEXT_ACTION.TITLE', 'Próxima ação') }}
      </span>

      <!-- Formulário -->
      <div v-if="editingNextAction" class="flex flex-col gap-2">
        <input
          v-model="naForm.due_on"
          type="date"
          class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-n-brand"
        />
        <div class="relative w-full">
          <select
            v-model="naForm.type"
            class="reset-base appearance-none w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-n-brand ltr:pr-8 rtl:pl-8"
          >
            <option v-for="opt in naTypes" :key="opt.id" :value="opt.id">
              {{ opt.label }}
            </option>
          </select>
          <span
            class="absolute top-1/2 -translate-y-1/2 pointer-events-none i-lucide-chevron-down size-4 text-n-slate-10 ltr:right-2.5 rtl:left-2.5"
          />
        </div>
        <input
          v-model="naForm.note"
          type="text"
          :placeholder="
            tp(
              'DEAL_RECORD.NEXT_ACTION.NOTE_PLACEHOLDER',
              'Ex.: ligar para fechar'
            )
          "
          class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-n-brand"
        />
        <div class="flex justify-end gap-2">
          <Button
            v-if="nextAction"
            variant="clear"
            size="xs"
            color="ruby"
            :label="tp('DEAL_RECORD.NEXT_ACTION.CLEAR', 'Remover')"
            @click="clearNextAction"
          />
          <Button
            variant="clear"
            size="xs"
            :label="tp('COMMON.CANCEL', 'Cancelar')"
            @click="editingNextAction = false"
          />
          <Button
            color="teal"
            size="xs"
            :is-loading="savingNextAction"
            :disabled="!naForm.due_on"
            :label="tp('COMMON.SAVE', 'Salvar')"
            @click="saveNextAction"
          />
        </div>
      </div>

      <!-- Ação definida -->
      <div
        v-else-if="nextAction && !nextAction.done"
        class="flex items-center gap-2 rounded-lg border p-2.5"
        :class="{
          'border-n-ruby-6 bg-n-ruby-3/40': naStatus === 'overdue',
          'border-n-amber-6 bg-n-amber-3/40': naStatus === 'today',
          'border-n-weak bg-n-background': naStatus === 'upcoming',
        }"
      >
        <button
          type="button"
          class="shrink-0 text-n-slate-10 hover:text-n-teal-11"
          :title="tp('DEAL_RECORD.NEXT_ACTION.MARK_DONE', 'Concluir')"
          @click="markNextActionDone"
        >
          <span class="i-lucide-circle size-4" />
        </button>
        <button
          type="button"
          class="flex flex-1 min-w-0 flex-col text-left"
          @click="openNextActionEdit"
        >
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ naTypeLabel(nextAction.type)
            }}<template v-if="nextAction.note">
              — {{ nextAction.note }}
            </template>
          </span>
          <span
            class="text-xs"
            :class="{
              'text-n-ruby-11 font-medium': naStatus === 'overdue',
              'text-n-amber-11 font-medium': naStatus === 'today',
              'text-n-slate-10': naStatus === 'upcoming',
            }"
          >
            {{
              naStatus === 'overdue'
                ? tp('DEAL_RECORD.NEXT_ACTION.OVERDUE', 'Vencida')
                : naStatus === 'today'
                  ? tp('DEAL_RECORD.NEXT_ACTION.TODAY', 'Hoje')
                  : formatNaDate(nextAction.due_on)
            }}
          </span>
        </button>
      </div>

      <!-- Sem ação definida -->
      <button
        v-else
        type="button"
        class="flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-n-weak px-2.5 py-2 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
        @click="openNextActionEdit"
      >
        <span class="i-lucide-plus size-3.5" />
        {{ tp('DEAL_RECORD.NEXT_ACTION.ADD', 'Definir próxima ação') }}
      </button>
    </div>

    <!-- Agente atribuído -->
    <div class="p-3">
      <div class="flex items-center justify-between mb-1.5">
        <span
          class="flex items-center gap-1.5 text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
        >
          <span class="i-lucide-user-round size-3.5" />
          {{ t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL') }}
        </span>
        <Button
          v-if="showSelfAssign"
          link
          xs
          icon="i-lucide-arrow-right"
          class="!gap-1"
          :label="t('CONVERSATION_SIDEBAR.SELF_ASSIGN')"
          @click="onSelfAssign"
        />
      </div>
      <MultiselectDropdown
        :options="agentsList"
        :selected-item="assignedAgent"
        :multiselector-title="t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @select="onAssignAgent"
      />
    </div>

    <!-- Time atribuído -->
    <div class="p-3">
      <span
        class="flex items-center gap-1.5 mb-1.5 text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
      >
        <span class="i-lucide-users size-3.5" />
        {{ t('CONVERSATION_SIDEBAR.TEAM_LABEL') }}
      </span>
      <MultiselectDropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        :multiselector-title="t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
        "
        @select="onAssignTeam"
      />
    </div>

    <!-- Prioridade -->
    <div class="p-3">
      <span
        class="flex items-center gap-1.5 mb-1.5 text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
      >
        <span class="i-lucide-flag size-3.5" />
        {{ t('CONVERSATION.PRIORITY.TITLE') }}
      </span>
      <MultiselectDropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="
          t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')
        "
        :no-search-result="
          t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')
        "
        :input-placeholder="
          t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')
        "
        @select="onAssignPriority"
      />
    </div>

    <!-- Etiquetas -->
    <div class="p-3">
      <span
        class="flex items-center gap-1.5 mb-2 text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
      >
        <span class="i-lucide-tag size-3.5" />
        {{ t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS') }}
      </span>
      <LabelBox :conversation-id="conversationId" />
    </div>

    <!-- Funil e etapa: mover etapa, trocar/adicionar funil -->
    <div class="flex flex-col gap-2.5 p-3">
      <span
        class="flex items-center gap-1.5 text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
      >
        <span class="i-lucide-git-fork size-3.5 rotate-90" />
        {{ tp('MULTIPLE_FUNNELS.TITLE', 'Funil e etapa') }}
      </span>

      <!-- Cada negócio: funil + seletor de etapa -->
      <div
        v-for="mapping in mappings"
        :key="mapping.id"
        class="flex flex-col gap-1.5 rounded-lg border border-n-weak bg-n-background p-2.5"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ mapping.funnel_name }}
          </span>
          <button
            type="button"
            class="text-n-slate-10 hover:text-n-ruby-11"
            :title="tp('MULTIPLE_FUNNELS.REMOVE.TITLE', 'Remover deste funil')"
            @click="removeFromFunnel(mapping)"
          >
            <span class="i-lucide-x size-3.5" />
          </button>
        </div>
        <div class="relative w-full">
          <select
            :value="mapping.stage_name"
            class="reset-base appearance-none w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 outline-none focus:border-n-brand ltr:pr-8 rtl:pl-8"
            @change="moveStage(mapping, $event.target.value, $event.target)"
          >
            <option
              v-for="stage in stagesForFunnel(mapping.funnel_id)"
              :key="stage.key"
              :value="stage.key"
            >
              {{ stage.label }}
            </option>
          </select>
          <span
            class="absolute top-1/2 -translate-y-1/2 pointer-events-none i-lucide-chevron-down size-4 text-n-slate-10 ltr:right-2.5 rtl:left-2.5"
          />
        </div>
      </div>

      <!-- Adicionar a outro funil -->
      <button
        v-if="!showAddFunnel && availableFunnels.length > 0"
        type="button"
        class="flex items-center justify-center gap-1 px-2.5 py-2 text-xs font-medium rounded-lg border border-dashed border-n-weak text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
        @click="showAddFunnel = true"
      >
        <span class="i-lucide-plus size-3.5" />
        {{ tp('MULTIPLE_FUNNELS.ADD.BUTTON', 'Adicionar a outro funil') }}
      </button>

      <div
        v-if="showAddFunnel"
        class="flex flex-col gap-2 rounded-lg border border-n-weak bg-n-background p-2.5"
      >
        <div class="relative w-full">
          <select
            v-model="addFunnelId"
            class="reset-base appearance-none w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 outline-none focus:border-n-brand ltr:pr-8 rtl:pl-8"
            @change="addStage = null"
          >
            <option :value="null">
              {{
                tp('MULTIPLE_FUNNELS.ADD.SELECT_FUNNEL', 'Selecione o funil')
              }}
            </option>
            <option
              v-for="funnel in availableFunnels"
              :key="funnel.id"
              :value="funnel.id"
            >
              {{ funnel.name }}
            </option>
          </select>
          <span
            class="absolute top-1/2 -translate-y-1/2 pointer-events-none i-lucide-chevron-down size-4 text-n-slate-10 ltr:right-2.5 rtl:left-2.5"
          />
        </div>
        <div v-if="addFunnelId" class="relative w-full">
          <select
            v-model="addStage"
            class="reset-base appearance-none w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 outline-none focus:border-n-brand ltr:pr-8 rtl:pl-8"
          >
            <option :value="null">
              {{ tp('MULTIPLE_FUNNELS.ADD.SELECT_STAGE', 'Selecione a etapa') }}
            </option>
            <option
              v-for="stage in addFunnelStages"
              :key="stage.key"
              :value="stage.key"
            >
              {{ stage.label }}
            </option>
          </select>
          <span
            class="absolute top-1/2 -translate-y-1/2 pointer-events-none i-lucide-chevron-down size-4 text-n-slate-10 ltr:right-2.5 rtl:left-2.5"
          />
        </div>
        <div class="flex justify-end gap-2">
          <Button
            variant="clear"
            size="xs"
            :label="tp('COMMON.CANCEL', 'Cancelar')"
            @click="showAddFunnel = false"
          />
          <Button
            color="teal"
            size="xs"
            :is-loading="savingFunnel"
            :disabled="!addFunnelId || !addStage"
            :label="tp('MULTIPLE_FUNNELS.ADD.CONFIRM', 'Adicionar')"
            @click="confirmAddFunnel"
          />
        </div>
      </div>
    </div>

    <!-- Agendamentos por último (pedido do Ivan: depois do funil, bem abaixo).
         Agenda já amarrado ao cliente da conversa — convite e lembretes saem
         pelo pipeline da Agenda. -->
    <div v-if="conversationId" class="deal-appointments">
      <ConversationAppointments :conversation-id="conversationId" />
    </div>
    <TerminalStageCloseModal
      :show="!!pendingClose"
      :kind="pendingClose?.kind"
      :mapping="pendingClose?.mapping"
      :conversation="conversation"
      @close="pendingClose = null"
      @closed="onDealClosed"
    />
  </div>
</template>
